import { Navigate, useLocation } from 'react-router';
import { WifiOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, skip onboarding check (used for onboarding route itself) */
  skipOnboardingCheck?: boolean;
}

export default function ProtectedRoute({ children, skipOnboardingCheck = false }: ProtectedRouteProps) {
  const { user, loading, authUnreachable } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-subtle flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Reachability is checked BEFORE `!user`, because when the API is down there is no user
  // to have — and sending someone to the login page then tells them the wrong thing
  // entirely: that they were signed out. They were not. Their session is intact and the
  // cookie is untouched; the server just could not answer. Saying so, with a retry, is the
  // honest version, and it is what a slow menu import returning 502 used to get wrong.
  if (authUnreachable && !user) {
    return (
      <div className="min-h-screen bg-surface-subtle flex items-center justify-center px-6">
        <div className="card-premium max-w-sm w-full p-8 text-center">
          <div className="size-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <WifiOff className="w-6 h-6 text-amber-500" />
          </div>
          <p className="text-ink font-bold mb-1">Can&apos;t reach the server</p>
          <p className="text-ink-muted text-sm mb-5">
            You&apos;re still signed in — we just couldn&apos;t load your account. This usually
            clears in a moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-gradient h-10 px-5 rounded-xl text-sm font-semibold text-white"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (!skipOnboardingCheck && !user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
